import { CrossFrameMessenger, ErrorCodes, InitializationOptions, VerbId, VersionNumber } from '@tableau/api-internal-contract-js';
import { Instance, PresentationLayer } from '@tableau/preslayer-api-contract';
import { ApiContext } from '../ApiContext';
import { AddInInstancePresModel, InternalTableauError } from '../ApiCore';
import { Disposable } from '../Disposable';
import { PresLayerOptionalCompatibility } from '../mappings/PresLayerOptionalCompatibility';
import { InitializationHandler } from './InitializationHandler';
import { PresLayerHandler } from './PresLayerHandler';
import { UINamespaceOptions } from './UINamespaceHandler';

/**
 * This class is the primary export of the server bootstrap project. When an ApiDispatcher is
 * initialized, it will be responsible for initializing and communicating between Tableau's preslayer
 * and the Tableau js library the third-party developer has created
 */
export class ApiDispatcher implements Disposable {
  private _initializationHandler: InitializationHandler;
  private _presLayerHandler?: PresLayerHandler;
  private _isInitialized = false;

  private _overrideMessageTarget?: string;

  /**
   * Creates an instance of ApiDispatcher for the specified extension instance
   * @param _extensionInstancePresModel info about the extensions this dispatcher will be working with
   * @param _presLayer An initialized Tableau presentation layer interface
   * @param _thisWindow The window in which this dispatcher exists
   * @param _uiNamespaceOptions An object containing properties needed for opening/closing a UINamespace dialog
   */
  public constructor(
    private _extensionInstancePresModel: AddInInstancePresModel,
    private _presLayer: PresentationLayer = Instance,
    private _thisWindow: Window | null = window,
    private _uiNamespaceOptions?: UINamespaceOptions,
  ) {
    // Create our initialization handler to listen for messages from the extension
    this._initializationHandler = new InitializationHandler(this._thisWindow!, this.onInitializationComplete.bind(this));
  }

  /**
   * Sets the iframe which is hosting the extension. Since we might be creating this class before
   * we have access to this frame, but after the frame has posted a message to us, we need to set the
   * extension frame outside of the constructor.
   *
   * @param extensionFrame The frame where the extension is running
   */
  public setExtensionFrame(extensionFrame: HTMLIFrameElement): void {
    // not sure when this might happen, but the types don't lie. Better be noisy here!
    if (!extensionFrame.contentWindow) {
      throw new InternalTableauError(ErrorCodes.INTERNAL_ERROR, "Extension frame's contentWindow was null.");
    }

    this.setExtensionWindow(extensionFrame.contentWindow);
  }

  /**
   * Sets the window that is running the extension to be communicated with by this APIDispatcher.
   */
  public setExtensionWindow(extensionDialogWindow: Window): void {
    // Just pass this down to the initialization handler
    this._initializationHandler.receivedExtensionFrame(extensionDialogWindow);
  }

  /**
   * Overrides the target of postMessage. Useful for tests with relative file URLs. See usage in
   * ExtensionBrowserWidget::MakeInteropConfig().
   */
  public setOverrideMessageTarget(messageTarget: string): void {
    this._overrideMessageTarget = messageTarget;
  }

  /**
   * Dispose of anything that is no longer needed and may cause problems to have lingering around once the extension is gone
   */
  public dispose(): void {
    if (this._presLayerHandler) {
      this._presLayerHandler.dispose();
    }
    // stale references to this._thisWindow may cause cross domain issues on ie, so set it to null to be safe (TFS 822503)
    this._thisWindow = null;
  }

  public get isInitialized(): boolean {
    return this._isInitialized && !!this._presLayerHandler;
  }

  private onInitializationComplete(
    versionNumber: VersionNumber,
    initializationMessageGuid: string,
    extensionFrame: Window,
    options?: InitializationOptions,
  ): void {
    // Create a CrossFrameMessenger which will communicate between this window, the extension frame, and limit messages
    // to only be passed to the url we were told our extension is hosted on.

    // Determine the extension URL which is the target for postMessage in CrossFrameMessenger.
    // This is the actual url that is loaded. Use the overridden value if there is one.
    // For first class extensions, setOverrideMessageTarget is called (@see ExtensionZoneView.cs & ExtensionController.cpp)
    const loadedExtensionUrl =
      this._overrideMessageTarget ||
      PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(this._extensionInstancePresModel.addInRegistrationPresModel).url;
    const messenger = new CrossFrameMessenger(this._thisWindow!, extensionFrame, loadedExtensionUrl);

    // Initialize our pres layer handler with the messenger
    this._presLayerHandler = new PresLayerHandler(
      this._presLayer,
      this._extensionInstancePresModel,
      loadedExtensionUrl,
      versionNumber,
      messenger,
      this._uiNamespaceOptions,
    );

    const extensionLocator = this._extensionInstancePresModel.addInLocatorPresModel;
    // Send a message back to the extension frame so it knows we got the initialize message.
    messenger.prepareCommandResponseMessage(initializationMessageGuid, extensionLocator, undefined).send();

    this._isInitialized = true;

    // NOTE: This block was added in 2019.3 of product. Previous versions of Tableau are handled through
    //  external-api-js (LegacyInternalApiDispatcherHolder.ts).
    // TFSID: 1091561 tracks an improvement to this system to make it less complex and tie it directly to
    //  tableau beta release versions.
    if (!options || typeof options.isAlpha === 'undefined') {
      // tslint:disable-next-line:no-console
      console.warn('This is a beta version of the Extensions API. Please upgrade to an official release.');
    } else if (options && options.isAlpha && ApiContext.getInstance().isPlatformOfficialRelease()) {
      // NOTE: ignoring the success / failure of this block command. Potential opportunity for failure instrumentation here.
      this._presLayerHandler.execute(VerbId.BlockExtension, {}).then().catch();
      this._isInitialized = false;
    }
  }
}
